export const SINGAPORE_TIME_ZONE = "Asia/Singapore";

type DateLike = Date | string | number | null | undefined;

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function isIsoDateOnly(value: string): boolean {
  return ISO_DATE_RE.test(value.trim());
}

function instantFromValue(value: Date | string | number): Date | null {
  if (typeof value === "string" && isIsoDateOnly(value)) {
    return new Date(`${value.trim()}T12:00:00.000+08:00`);
  }
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toValidDate(value: DateLike): Date | null {
  if (value == null) return null;
  return instantFromValue(value);
}

export function formatSingaporeDate(
  value: DateLike,
  options: Intl.DateTimeFormatOptions = { day: "numeric", month: "long", year: "numeric" },
  locale = "en-SG"
): string {
  const d = toValidDate(value);
  if (!d) return "—";
  return d.toLocaleDateString(locale, { ...options, timeZone: SINGAPORE_TIME_ZONE });
}

export function formatSingaporeDateTime(
  value: DateLike,
  options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  },
  locale = "en-SG"
): string {
  const d = toValidDate(value);
  if (!d) return "—";
  return d.toLocaleString(locale, { ...options, timeZone: SINGAPORE_TIME_ZONE });
}

/** Build an anchored Date from a yyyy-mm-dd local SGT calendar date (noon SGT). */
export function singaporeDateFromIsoDate(isoDate: string): Date {
  const key = isoDate.trim();
  if (!isIsoDateOnly(key)) {
    throw new Error(`Invalid Singapore date input: ${isoDate}`);
  }
  return new Date(`${key}T12:00:00.000+08:00`);
}

/** Singapore calendar day as `YYYY-MM-DD` from a UTC instant (no manual +8h shift). */
export function getSingaporeDateKeyFromInstant(value: DateLike): string | null {
  if (value == null) return null;
  if (typeof value === "string" && isIsoDateOnly(value)) {
    return value.trim();
  }
  const d = instantFromValue(value as Date | string | number);
  if (!d) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: SINGAPORE_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const byType = new Map(parts.map((p) => [p.type, p.value]));
  const year = byType.get("year");
  const month = byType.get("month");
  const day = byType.get("day");
  if (!year || !month || !day) return null;
  return `${year}-${month}-${day}`;
}

/** Noon-anchored Date for the Singapore calendar day of an instant (grouping / isSameDay). */
export function singaporeCalendarDayFromInstant(value: DateLike): Date | null {
  const key = getSingaporeDateKeyFromInstant(value);
  if (!key) return null;
  return singaporeDateFromIsoDate(key);
}

/** `<input type="date">` value for an instant — Singapore calendar day, never UTC ISO date. */
export function toSingaporeDateInputValue(value: DateLike): string {
  return getSingaporeDateKeyFromInstant(value) ?? "";
}

/** Parse `YYYY-MM-DD` from a date input as noon SGT (canonical appointment day encoding). */
export function parseSingaporeDateInput(yyyyMmDd: string): Date {
  return singaporeDateFromIsoDate(yyyyMmDd.trim());
}

/**
 * Write-path canon: collapse any encoding (local midnight, UTC midnight, noon SGT, YYYY-MM-DD)
 * to noon SGT of the Singapore calendar day.
 */
export function normalizeToSingaporeCalendarDay(value: DateLike): Date {
  const key = getSingaporeDateKeyFromInstant(value);
  if (!key) throw new Error("Invalid date for Singapore calendar-day normalize");
  return singaporeDateFromIsoDate(key);
}

export function singaporeDateKeysEqual(a: DateLike, b: DateLike): boolean {
  const ka = getSingaporeDateKeyFromInstant(a);
  const kb = getSingaporeDateKeyFromInstant(b);
  return ka != null && kb != null && ka === kb;
}

/** Minutes since Singapore local midnight (0–1439). */
export function getSingaporeMinutesOfDay(value: DateLike): number | null {
  const d = toValidDate(value);
  if (!d) return null;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: SINGAPORE_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const byType = new Map(parts.map((p) => [p.type, p.value]));
  let hour = Number(byType.get("hour") ?? NaN);
  const minute = Number(byType.get("minute") ?? NaN);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  // Some engines emit "24" for midnight.
  if (hour === 24) hour = 0;
  return hour * 60 + minute;
}

export function singaporeTodayDateKey(now: DateLike = new Date()): string {
  return getSingaporeDateKeyFromInstant(now) ?? "1970-01-01";
}

/** Add calendar days to a Singapore `YYYY-MM-DD` key (DST-safe via noon SGT anchors). */
export function addSingaporeCalendarDays(yyyyMmDd: string, days: number): string {
  const base = singaporeDateFromIsoDate(yyyyMmDd.trim());
  const shifted = new Date(base.getTime() + days * 24 * 60 * 60 * 1000);
  const key = getSingaporeDateKeyFromInstant(shifted);
  if (!key) throw new Error("Failed to add Singapore calendar days");
  return key;
}

export type SingaporeCalendarDayBounds = {
  /** YYYY-MM-DD 00:00:00.000 Asia/Singapore */
  startInclusive: Date;
  /** Next calendar day 00:00:00.000 Asia/Singapore (exclusive) */
  endExclusive: Date;
  dateKey: string;
};

/**
 * Query bounds for one Singapore calendar day: [00:00 SGT, next 00:00 SGT).
 *
 * Distinct from noon-SGT calendar-date anchors (`singaporeDateFromIsoDate` /
 * `coerceValidDay`). Use this for SQL/API ranges; use noon anchors for day identity.
 */
export function singaporeCalendarDayBounds(day: DateLike = new Date()): SingaporeCalendarDayBounds {
  const dateKey = getSingaporeDateKeyFromInstant(day);
  if (!dateKey) throw new Error("Invalid date for Singapore calendar day bounds");
  const startInclusive = new Date(`${dateKey}T00:00:00.000+08:00`);
  const endExclusive = new Date(startInclusive.getTime() + 24 * 60 * 60 * 1000);
  return { startInclusive, endExclusive, dateKey };
}

export type SingaporeCalendarRangeBounds = {
  startInclusive: Date;
  endExclusive: Date;
  firstDateKey: string;
  lastDateKey: string;
};

/**
 * Half-open SGT range covering an inclusive first..last calendar-day span:
 * [first 00:00 SGT, day-after-last 00:00 SGT).
 */
export function singaporeCalendarRangeBounds(
  firstDay: DateLike,
  lastDayInclusive: DateLike,
): SingaporeCalendarRangeBounds {
  const first = singaporeCalendarDayBounds(firstDay);
  const last = singaporeCalendarDayBounds(lastDayInclusive);
  if (last.startInclusive.getTime() < first.startInclusive.getTime()) {
    throw new Error("singaporeCalendarRangeBounds: last day is before first day");
  }
  return {
    startInclusive: first.startInclusive,
    endExclusive: last.endExclusive,
    firstDateKey: first.dateKey,
    lastDateKey: last.dateKey,
  };
}

/** True when instant falls in [00:00, next 00:00) SGT of `day`. */
export function isInstantInSingaporeCalendarDay(instant: DateLike, day: DateLike): boolean {
  const d = toValidDate(instant);
  if (!d) return false;
  const { startInclusive, endExclusive } = singaporeCalendarDayBounds(day);
  const t = d.getTime();
  return t >= startInclusive.getTime() && t < endExclusive.getTime();
}

/** Inclusive start / exclusive end UTC range covering one Singapore calendar day. */
export function getSingaporeDayRangeUtc(day: DateLike = new Date()): {
  startUtc: Date;
  endUtc: Date;
  dateKey: string;
} {
  const { startInclusive, endExclusive, dateKey } = singaporeCalendarDayBounds(day);
  return { startUtc: startInclusive, endUtc: endExclusive, dateKey };
}

/** Monday–Friday in Asia/Singapore (never UTC weekday slicing). */
export function isSingaporeWeekday(now: DateLike = new Date()): boolean {
  const d = toValidDate(now);
  if (!d) return false;
  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone: SINGAPORE_TIME_ZONE,
    weekday: "short",
  }).format(d);
  return weekday !== "Sat" && weekday !== "Sun";
}
